import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, computed, signal } from '@angular/core';
import {
  labelForWorkType,
  labelForSourceType,
} from '../../../../project-corpus/domain/openalex-types';
import { NumberFormatPipe } from '../../../../global/number-format.pipe';
import { ImportSizeEstimate } from '../../../../project-corpus/domain/types';

/**
 * Renders the result of a `previewOpenAlex` call: total count, optional
 * type-filter recap, and `Confirm` / `Back` buttons.
 *
 * Responsive behaviour:
 *  - The footer button row is `flex-col` on mobile and `sm:flex-row` on
 *    desktop so the primary action is full-width on phones.
 */
@Component({
  selector: 'app-openalex-preview',
  standalone: true,
  imports: [CommonModule, NumberFormatPipe],
  templateUrl: './openalex-preview.component.html',
  styleUrls: ['./openalex-preview.component.scss'],
})
export class OpenAlexPreviewComponent {
  private readonly _preview = signal<ImportSizeEstimate | null>(null);
  private readonly _query = signal('');
  private readonly _workTypes = signal<string[]>([]);
  private readonly _sourceTypes = signal<string[]>([]);
  private readonly _yearFrom = signal<number | null>(null);
  private readonly _yearTo = signal<number | null>(null);
  private readonly _language = signal<string[]>([]);

  @Input({ required: true })
  set preview(value: ImportSizeEstimate) {
    this._preview.set(value);
  }
  get preview(): ImportSizeEstimate {
    return this._preview() as ImportSizeEstimate;
  }

  @Input()
  set query(value: string) {
    this._query.set(value);
  }
  get query(): string {
    return this._query();
  }

  @Input()
  set workTypes(value: string[]) {
    this._workTypes.set(value ?? []);
  }
  get workTypes(): string[] {
    return this._workTypes();
  }

  @Input()
  set sourceTypes(value: string[]) {
    this._sourceTypes.set(value ?? []);
  }
  get sourceTypes(): string[] {
    return this._sourceTypes();
  }

  @Input()
  set yearFrom(value: number | null) {
    this._yearFrom.set(value);
  }
  get yearFrom(): number | null {
    return this._yearFrom();
  }

  @Input()
  set yearTo(value: number | null) {
    this._yearTo.set(value);
  }
  get yearTo(): number | null {
    return this._yearTo();
  }

  @Input()
  set language(value: string[]) {
    this._language.set(value ?? []);
  }
  get language(): string[] {
    return this._language();
  }

  @Output() confirm = new EventEmitter<void>();
  @Output() back = new EventEmitter<void>();

  @Input() submitting = false;

  readonly currentPreview = this._preview.asReadonly();
  readonly currentQuery = this._query.asReadonly();

  readonly isTooBroad = computed(() => {
    const val = this._preview();
    return val !== null && val.estimatedCount > val.maxAllowed;
  });

  readonly importDisabled = computed(() => {
    return this.isTooBroad() || this.submitting || (this._preview()?.estimatedCount ?? 0) === 0;
  });

  readonly filterRecap = computed(() => {
    const wt = this._workTypes();
    const st = this._sourceTypes();
    const yf = this._yearFrom();
    const yt = this._yearTo();
    const lang = this._language();
    const parts: string[] = [];
    if (wt.length > 0) {
      parts.push(`types: ${wt.map(labelForWorkType).join(', ')}`);
    }
    if (st.length > 0) {
      parts.push(`venues: ${st.map(labelForSourceType).join(', ')}`);
    }
    if (yf != null || yt != null) {
      const from = yf ?? '…';
      const to = yt ?? '…';
      parts.push(`year: ${from}–${to}`);
    }
    if (lang.length > 0) {
      parts.push(`language: ${lang.join(', ')}`);
    }
    return parts.length > 0 ? `Filtered by ${parts.join('; ')}` : null;
  });

  onConfirm(): void {
    this.confirm.emit();
  }

  onBack(): void {
    this.back.emit();
  }
}
